import type {
  ComponentPropsWithRef,
  ElementType,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { cx } from '../../cx'

type ListProps = PropsWithChildren<{
  className?: string
  ref?: Ref<HTMLUListElement>
}>

export function List({ ref, children, className, ...rest }: ListProps) {
  return (
    <ul ref={ref} className={className} {...rest}>
      {children}
    </ul>
  )
}

type ListItemGroupProps = { label?: string }

export function ListItemGroup({ children, label }: PropsWithChildren<ListItemGroupProps>) {
  return (
    <li>
      {label && <span className="font-base-copy text-secondary-text">{label}</span>}
      <ul>{children}</ul>
    </li>
  )
}

export type ListItemProps = VariantProps<typeof listItem> & {
  className?: string
  subline?: string
  selected?: boolean
  renderIcon?: (selected?: boolean) => ReactNode
  renderIconEnd?: (selected?: boolean) => ReactNode
  onClick?: (event: MouseEvent<HTMLLIElement>) => void
}

type ListItemAsProps<TAs extends ElementType> = {
  contentAs?: TAs
  contentAsProps?: ComponentPropsWithRef<ElementType extends TAs ? 'button' : TAs>
}

export function ListItem<TAs extends ElementType>({
  ref,
  children,
  renderIcon,
  renderIconEnd,
  onClick,
  variant,
  subline,
  selected,
  rounded,
  className,
  contentAs,
  contentAsProps,
  interactive = true,
  ...rest
}: PropsWithChildren<ListItemProps> & ListItemAsProps<TAs> & { ref?: Ref<HTMLLIElement> }) {
  const As = contentAs ?? (!interactive ? 'div' : 'button')
  const {
    root,
    content,
    label,
    icon,
    iconEnd,
    subline: sublineStyles,
  } = listItem({
    variant,
    rounded,
    interactive: interactive ?? As === 'button',
  })

  const showSubline = variant === 'extended' && subline

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- TODO: fix accessability
    <li ref={ref} {...rest} onClick={onClick} className={root()}>
      <As className={cx(content(), className)} {...contentAsProps}>
        {renderIcon && <span className={icon()}>{renderIcon(selected)}</span>}
        <div className="flex w-full flex-col self-center">
          <span className={label()}>{children}</span>
          {showSubline && <span className={sublineStyles()}>{subline}</span>}
        </div>
        {renderIconEnd && <span className={cx(icon(), iconEnd())}>{renderIconEnd(selected)}</span>}
      </As>
    </li>
  )
}

const listItem = tv({
  slots: {
    root: '',
    content: 'flex w-full p-2 text-left',
    label: 'font-base-label',
    subline: 'font-base-copy',
    icon: 'grid h-full shrink-0 place-content-center overflow-hidden rounded-xl [&:not(:has(img,svg))]:p-3 [&>svg]:size-6',
    iconEnd: '',
  },
  variants: {
    interactive: {
      true: {
        root: 'hover:bg-hover-light active:bg-active-light data-[focus]:bg-hover-light cursor-pointer',
      },
    },
    rounded: {
      true: { root: 'rounded-xl' },
    },
    variant: {
      default: { icon: 'size-12' },
      extended: {
        content: 'gap-4 px-4',
        label: 'font-heading5',
        icon: 'bg-sand size-14',
        iconEnd: 'bg-inherit',
      },
    },
  },
  defaultVariants: { variant: 'default', rounded: true, interactive: true },
})
